import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from "@material-ui/core";
import Loading from "../../Loading";
import MaterialModal from "../MaterialModal";
import { IAppState } from "../../../types/stores";
import { hideModal } from "../../../actions/actionCreators";
import withModal from "../withModal";
import {
    getDeployDestinationDiffCompanyList,
    addDeploymentDestination,
} from "../../../actions/DeploymentDestinationActions";
import { ICompanyDeploymentDestination } from "../../../types/stores/deploymentDestination";
import { getActiveServers } from "../../../reducers/deploymentDestination";

type Props = {
    isOpen: boolean;
};

const DestinationDetailModal: React.FC<Props> = ({ isOpen }: Props) => {
    const dispatch = useDispatch();

    const [isUpdating, setIsUpdating] = useState(false);

    const destinationDetailList = useSelector(
        (state: IAppState) => state.deploymentDestination.destinationsDiffCompany
    );
    const isLoading = useSelector((state: IAppState) => state.deploymentDestination.isLoading);
    const company = useSelector((state: IAppState) => state.companyEdit.company);
    const activeDeploymentServers = useSelector((state: IAppState) => getActiveServers(state));

    useEffect(() => {
        const companyId = company?.CompanyID;
        if (companyId && companyId > 0) {
            dispatch(getDeployDestinationDiffCompanyList(companyId.toString()));
        }
    }, [dispatch, company?.CompanyID]);

    const hide = () => {
        dispatch(hideModal("DESTINATION_DETAILS"));
        if (isUpdating) {
            setIsUpdating(false);
            window.location.assign("/ae/admin/companies/" + company?.CompanyID + "/destination");
        }
    };

    const handleAddToCompany = (data: ICompanyDeploymentDestination) => {
        setIsUpdating(true);
        const companyId = company?.CompanyID;
        let newDestination: ICompanyDeploymentDestination = {
            CompanyId: companyId?.toString()!,
            DeploymentOrder: data?.DeploymentOrder!,
            DestinationDescription: data?.DestinationDescription!,
            DestinationType: data?.DestinationType!,
            Folder: data?.Folder!,
            ServerDestination: data?.ServerDestination!,
            SFTPProfile: data?.SFTPProfile!,
            ShortenName: data?.ShortenName!,
            IsDestinationDisabled: data?.IsDestinationDisabled!,
            Id: data?.Id,
            DeploymentDestinationServerId: data?.DeploymentDestinationServerId,
        };
        dispatch(addDeploymentDestination(newDestination));
        dispatch(hideModal("DESTINATION_DETAILS"));
    };

    const renderDisabledColumn = (isDiabled: boolean) => (
        <>
            {isDiabled ? (
                <Tooltip title="Disabled">
                    <IconButton id="btn-diabled" color="secondary" size="small">
                        <i className={"material-icons material-icons-small-secondary"}>do_not_disturb</i>
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Enabled">
                    <IconButton id="btn-enabled" color="primary" size="small">
                        <i className={"material-icons material-icons-small-primary"}>check_circle</i>
                    </IconButton>
                </Tooltip>
            )}
        </>
    );

    const renderNewActionColumn = (data: ICompanyDeploymentDestination) => (
        <>
            <Tooltip title="Add">
                <IconButton id="btn-add" color="primary" onClick={() => handleAddToCompany(data)} size="small">
                    <i className={"material-icons material-icons-small-primary"}>add</i>
                </IconButton>
            </Tooltip>
        </>
    );

    const renderDestinationColumn = (row: ICompanyDeploymentDestination) => {
        if (row.DestinationType == "STS") {
            return row.SFTPProfile;
        } else if (row.DestinationType == "Server") {
            const server = activeDeploymentServers.find(x => x.Id == row.DeploymentDestinationServerId);
            if (server) {
                return server.Description;
            }
        }
        return "";
    };

    const getBody = () => (
        <div className="col-sm-16 text-nowrap" style={{ height: "450px" }}>
            Select a destination for the current company.
            {isLoading ? (
                <Loading />
            ) : destinationDetailList.length > 0 ? (
                <TableContainer style={{ maxHeight: "68vh" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" style={{ fontSize: "14px" }}>
                                    Destination Type
                                </TableCell>
                                <TableCell align="center" style={{ fontSize: "14px" }}>
                                    Destination
                                </TableCell>
                                <TableCell align="center" style={{ fontSize: "14px" }}>
                                    Folder
                                </TableCell>
                                <TableCell align="center" style={{ fontSize: "14px" }}>
                                    Description
                                </TableCell>
                                <TableCell align="center" style={{ fontSize: "14px" }}>
                                    Disabled
                                </TableCell>
                                <TableCell align="center" style={{ fontSize: "14px" }}>
                                    Add to Company
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {destinationDetailList.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell align="center" style={{ fontSize: "14px" }}>
                                        {row.DestinationType}
                                    </TableCell>
                                    <TableCell align="center" style={{ fontSize: "14px" }}>
                                        {renderDestinationColumn(row)}
                                    </TableCell>
                                    <TableCell align="center" style={{ fontSize: "14px" }}>
                                        {row.Folder}
                                    </TableCell>
                                    <TableCell align="center" style={{ fontSize: "14px" }}>
                                        {row.DestinationDescription}
                                    </TableCell>
                                    <TableCell align="center" style={{ fontSize: "14px" }}>
                                        {renderDisabledColumn(row.IsDestinationDisabled)}
                                    </TableCell>
                                    <TableCell align="center" style={{ fontSize: "14px" }}>
                                        {renderNewActionColumn(row)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <p>{"No Destination Details Data to display."}</p>
            )}
        </div>
    );

    return (
        <MaterialModal
            id={"edit-donor-modal"}
            isOpen={isOpen}
            onClose={() => hide()}
            title={"Destination details"}
            maxWidth="xl"
            fullWidth
            //onPrimaryButtonRequest={() => save()}
            //isPrimaryButtonDisabled={!canSave}
            showPrimaryButton={false}
            primaryButtonText="Save"
            cancelButtonText="Back"
        >
            {getBody()}
        </MaterialModal>
    );
};

export default withModal(DestinationDetailModal, "DESTINATION_DETAILS");
